// Phase 30 — Aufgaben (Tasks) v2 — office vs fahrer, claim/complete, dept-scoping
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SectionHeader, StatusBadge, EmptyState } from "@/components/Shared";
import EmployeePicker from "@/components/EmployeePicker";
import DepartmentPicker, { DepartmentBadge } from "@/components/DepartmentPicker";
import HelpHint from "@/components/HelpHint";
import FirmFilter from "@/components/FirmFilter";
import {
  Plus, X, Check, Trash2, Pencil, Hand, Truck, Briefcase,
  Users as UsersIcon, AlertCircle, CheckCircle2, Clock,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  description: "",
  notes: "",
  target_audience: "office",
  assigned_to_employee_id: "",
  department_id: "",
  status: "offen",
  priority: "normal",
  due_date: "",
  due_time: "",
};

const PRIO_COLOR = {
  niedrig: "bg-zinc-700 text-zinc-200",
  normal: "bg-blue-600/30 text-blue-200 border border-blue-500/40",
  hoch: "bg-amber-600/30 text-amber-200 border border-amber-500/40",
  kritisch: "bg-red-600/30 text-red-200 border border-red-500/40",
};

export default function Aufgaben() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState("offen");
  const [audienceFilter, setAudienceFilter] = useState("alle"); // alle | office | fahrer
  const [deptFilter, setDeptFilter] = useState("");
  const [sp] = useSearchParams();
  // Phase 32b — Drill-down from Dashboard
  useEffect(() => {
    const urlDept = sp.get("department_id");
    if (urlDept !== null) setDeptFilter(urlDept);
    const urlAudience = sp.get("target_audience");
    if (urlAudience && ["alle", "office", "fahrer"].includes(urlAudience)) setAudienceFilter(urlAudience);
    const urlOverdue = sp.get("overdue");
    if (urlOverdue === "1") setFilter("offen");
  }, [sp]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [summary, setSummary] = useState({ open: 0, mine_open: 0, unclaimed_office_open: 0, fahrer_pending: 0 });

  async function load() {
    const params = filter === "alle" ? {} : { status: filter };
    if (audienceFilter !== "alle") params.target_audience = audienceFilter;
    if (deptFilter) params.department_id = deptFilter;
    const [{ data: rows }, { data: sum }] = await Promise.all([
      api.get("/tasks", { params }),
      api.get("/tasks/_summary"),
    ]);
    setItems(rows || []);
    setSummary(sum || {});
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, audienceFilter, deptFilter]);
  useEffect(() => { api.get("/employees").then(({ data }) => setEmployees(data || [])); }, []);

  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  async function claim(id) {
    try { await api.post(`/tasks/${id}/claim`); toast.success("Aufgabe übernommen"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function release(id) {
    try { await api.post(`/tasks/${id}/release`); toast.success("Übernahme zurückgegeben"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function complete(id) {
    try { await api.post(`/tasks/${id}/complete`); toast.success("Erledigt markiert"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function reopen(id) {
    try { await api.patch(`/tasks/${id}/toggle`); toast.success("Wieder geöffnet"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Aufgabe wirklich löschen?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function save() {
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    if (form.target_audience === "fahrer" && !form.assigned_to_employee_id) {
      toast.error("Bitte einen Fahrer auswählen"); return;
    }
    try {
      if (editing) await api.put(`/tasks/${editing.id}`, form);
      else await api.post("/tasks", form);
      toast.success(editing ? "Aktualisiert" : "Angelegt");
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 md:p-8" data-testid="aufgaben-page">
      <SectionHeader title="Aufgaben" subtitle="Office + Fahrer · mit Übernahme- und Annahme-Workflow"
        actions={
          <button data-testid="add-task-btn" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4"/> Neue Aufgabe
          </button>
        }/>

      <HelpHint title="Wie funktionieren Aufgaben?" defaultOpen={false} className="mb-4" testid="help-aufgaben">
        <p>
          Aufgaben werden entweder dem <b className="text-amber-300">Büro</b> (Disponent / Backoffice / Buchhaltung)
          oder einem konkreten <b className="text-amber-300">Fahrer</b> zugewiesen.
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li><b>Office-Aufgabe ohne konkrete Person</b>: jeder Büro-Kollege mit Zugriff sieht sie und kann sie per Klick „Übernehmen". Nach Übernahme zeigt das System wer dran arbeitet.</li>
          <li><b>Office-Aufgabe mit Person</b>: die Person bekommt eine Glocken-Benachrichtigung — andere können erst übernehmen, wenn die Person zurückgibt.</li>
          <li><b>Fahrer-Aufgabe</b>: der gewählte Fahrer wird im Fahrer-Portal gesperrt, bis er „Annehmen" oder „Ablehnen" klickt. Bei Ablehnung muss er einen Grund angeben (mind. 5 Zeichen).</li>
          <li><b>Abteilung</b>: setzt die Sichtbarkeit. Ein Disponent für „Amazon" sieht nur Amazon-Aufgaben; Admin/Geschäftsführung sehen alles.</li>
          <li><b>Roter Counter</b> in der Sidebar zeigt offene Aufgaben in deinem Sichtbereich.</li>
          <li>Wer fertig ist klickt <b>Erledigt</b> — das System protokolliert wer und wann.</li>
        </ul>
      </HelpHint>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat icon={Clock} label="Offen gesamt" value={summary.open} tone="amber" testid="task-kpi-open"/>
        <Stat icon={Hand} label="Nicht übernommen" value={summary.unclaimed_office_open} tone="blue" testid="task-kpi-unclaimed"/>
        <Stat icon={Briefcase} label="Meine offen" value={summary.mine_open} tone="emerald" testid="task-kpi-mine"/>
        <Stat icon={Truck} label="Fahrer-Annahme offen" value={summary.fahrer_pending} tone="rose" testid="task-kpi-fahrer-pending"/>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="p-4 border-b border-zinc-800 flex gap-2 flex-wrap items-center">
          <FirmFilter compact />
          {deptFilter && (
            <DeptFilterChip id={deptFilter} onClear={() => setDeptFilter("")} />
          )}
          {[["offen", "Offen"], ["in_bearbeitung", "In Bearbeitung"], ["erledigt", "Erledigt"], ["abgelehnt", "Abgelehnt"], ["alle", "Alle"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-3 py-1.5 text-xs rounded font-medium ${filter === k ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
              {l}
            </button>
          ))}
          <span className="mx-2 text-zinc-700">|</span>
          {[["alle", "Alle"], ["office", "Office"], ["fahrer", "Fahrer"]].map(([k, l]) => (
            <button key={k} onClick={() => setAudienceFilter(k)} data-testid={`audience-${k}`}
              className={`px-3 py-1.5 text-xs rounded font-medium ${audienceFilter === k ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
              {l}
            </button>
          ))}
        </div>

        {items.length === 0 ? <EmptyState title="Keine Aufgaben" hint="Erstelle die erste Aufgabe."/> : (
          <div className="divide-y divide-zinc-800">
            {items.map((t) => (
              <TaskRow
                key={t.id}
                t={t}
                emp={empById[t.assigned_to_employee_id]}
                today={today}
                userId={user?.id}
                onClaim={() => claim(t.id)}
                onRelease={() => release(t.id)}
                onComplete={() => complete(t.id)}
                onReopen={() => reopen(t.id)}
                onEdit={() => { setEditing(t); setForm({ ...EMPTY, ...t }); setShowForm(true); }}
                onDelete={() => remove(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <TaskFormModal
          form={form}
          setForm={setForm}
          editing={editing}
          onSave={save}
          onClose={() => { setShowForm(false); setEditing(null); setForm(EMPTY); }}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const map = {
    amber: "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-200",
    blue: "bg-blue-500/10 border-blue-500/40 text-blue-700 dark:text-blue-200",
    emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-200",
    rose: "bg-rose-500/10 border-rose-500/40 text-rose-700 dark:text-rose-200",
  };
  return (
    <div className={`rounded-md border p-3 ${map[tone] || "bg-zinc-900 border-zinc-800"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-90 font-bold">{label}</span>
        <Icon className="w-4 h-4 opacity-90"/>
      </div>
      <div className="mt-1 text-3xl font-extrabold tabular-nums">{value || 0}</div>
    </div>
  );
}

function TaskRow({ t, emp, today, userId, onClaim, onRelease, onComplete, onReopen, onEdit, onDelete }) {
  const overdue = t.status !== "erledigt" && t.due_date && t.due_date < today;
  const isFahrer = t.target_audience === "fahrer";
  const claimed = !!t.claimed_by_user_id;
  const mine = claimed && t.claimed_by_user_id === userId;
  return (
    <div className={`px-4 py-3 ${overdue ? "bg-red-500/5" : ""}`} data-testid={`task-${t.id}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
          isFahrer ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                   : "bg-blue-500/15 text-blue-300 border border-blue-500/30"}`}>
          {isFahrer ? <Truck className="w-4 h-4"/> : <Briefcase className="w-4 h-4"/>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className={`text-sm ${t.status === "erledigt" ? "text-zinc-500 line-through" : "text-zinc-100 font-medium"}`}>{t.title}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIO_COLOR[t.priority] || PRIO_COLOR.normal}`}>{t.priority}</span>
            <StatusBadge status={t.status === "in_bearbeitung" ? "in arbeit" : t.status}/>
            {isFahrer && t.driver_response === "pending" && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40">
                <AlertCircle className="w-3 h-3"/> Warten auf Annahme
              </span>
            )}
            {isFahrer && t.driver_response === "rejected" && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">
                <X className="w-3 h-3"/> Vom Fahrer abgelehnt
              </span>
            )}
            {isFahrer && t.driver_response === "accepted" && t.status !== "erledigt" && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                <Check className="w-3 h-3"/> Vom Fahrer angenommen
              </span>
            )}
            <DepartmentBadge id={t.department_id}/>
          </div>
          {t.description && <div className="text-xs text-zinc-400 mt-1">{t.description}</div>}
          {t.notes && <div className="text-[11px] text-zinc-500 italic mt-0.5">Notiz: {t.notes}</div>}

          <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-500 flex-wrap">
            {isFahrer && emp && (
              <span className="inline-flex items-center gap-1 text-zinc-300">
                <UsersIcon className="w-3 h-3"/> Fahrer: <b>{emp.first_name} {emp.last_name}</b>
              </span>
            )}
            {!isFahrer && claimed && (
              <span className="inline-flex items-center gap-1 text-zinc-300">
                <Hand className="w-3 h-3"/> Übernommen von <b>{t.claimed_by_user_name}</b>
              </span>
            )}
            {!isFahrer && !claimed && (
              <span className="text-amber-300">Noch nicht übernommen</span>
            )}
            {t.due_date && <span className={overdue ? "text-red-400 font-semibold" : ""}>Fällig: {t.due_date}{t.due_time ? ` ${t.due_time}` : ""}</span>}
            {t.done_by_user_name && <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3 h-3"/> Erledigt von <b>{t.done_by_user_name}</b></span>}
            {isFahrer && t.driver_response === "rejected" && t.driver_response_reason && (
              <span className="text-red-400 italic">„{t.driver_response_reason}"</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {!isFahrer && !claimed && t.status !== "erledigt" && (
            <button onClick={onClaim} data-testid={`claim-${t.id}`}
                    className="text-xs px-2.5 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold inline-flex items-center gap-1">
              <Hand className="w-3 h-3"/> Übernehmen
            </button>
          )}
          {!isFahrer && mine && t.status !== "erledigt" && (
            <button onClick={onRelease} data-testid={`release-${t.id}`}
                    className="text-xs px-2.5 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-100 inline-flex items-center gap-1">
              Zurück
            </button>
          )}
          {t.status !== "erledigt" && (!isFahrer || t.driver_response === "accepted") && (
            <button onClick={onComplete} data-testid={`complete-${t.id}`}
                    className="text-xs px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1">
              <Check className="w-3 h-3"/> Erledigt
            </button>
          )}
          {t.status === "erledigt" && (
            <button onClick={onReopen} data-testid={`reopen-${t.id}`}
                    className="text-xs px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
              Öffnen
            </button>
          )}
          <button onClick={onEdit} className="p-1.5 text-zinc-400 hover:text-blue-400" data-testid={`edit-task-${t.id}`}><Pencil className="w-3.5 h-3.5"/></button>
          <button onClick={onDelete} className="p-1.5 text-zinc-400 hover:text-red-400" data-testid={`delete-task-${t.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
        </div>
      </div>
    </div>
  );
}

function TaskFormModal({ form, setForm, editing, onSave, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50">{editing ? "Aufgabe bearbeiten" : "Neue Aufgabe"}</h3>
          <button onClick={onClose} className="text-zinc-400" data-testid="task-form-close"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          <HelpHint title="Wie weise ich richtig zu?" testid="help-task-form">
            <p>1. Wähle <b>An wen?</b> — entweder „Office" (jemand aus dem Büro) oder „Fahrer" (ein konkreter Fahrer).</p>
            <p>2. Setze die <b>Abteilung</b> — sie filtert sowohl die Sichtbarkeit für Disponenten als auch die Mitarbeiter-Auswahl unten.</p>
            <p>3. Wähle die <b>Person</b> aus der gefilterten Liste (nur aktive Mitarbeiter, passende Rolle, passende Abteilung).</p>
            <p>4. <b>Office</b> + leer „Spezifische Person" = jeder Office-Kollege im Sichtbereich kann sie übernehmen.</p>
            <p>5. <b>Fahrer</b> = der Fahrer wird im Portal gesperrt, bis er Annehmen/Ablehnen klickt.</p>
          </HelpHint>

          {/* Audience toggle */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-2">An wen? *</label>
            <div className="grid grid-cols-2 gap-2">
              <AudienceToggle active={form.target_audience === "office"}
                              icon={Briefcase} label="Office" sub="Disponenten / Backoffice"
                              tone="blue" testid="audience-toggle-office"
                              onClick={() => setForm({ ...form, target_audience: "office", assigned_to_employee_id: "" })}/>
              <AudienceToggle active={form.target_audience === "fahrer"}
                              icon={Truck} label="Fahrer" sub="Konkreter Fahrer mit Annahme-Pflicht"
                              tone="rose" testid="audience-toggle-fahrer"
                              onClick={() => setForm({ ...form, target_audience: "fahrer", assigned_to_employee_id: "" })}/>
            </div>
          </div>

          {/* Department — must be set BEFORE picking the person so the picker can filter */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Abteilung</label>
            <DepartmentPicker value={form.department_id || ""}
                              onChange={(id) => setForm({ ...form, department_id: id, assigned_to_employee_id: "" })}
                              testid="task-department-picker"/>
            <p className="text-[10px] text-zinc-500 mt-1">
              Steuert die Sichtbarkeit für Disponenten <b>und</b> filtert die Mitarbeiter-Auswahl unten.
            </p>
          </div>

          {/* Assigned person — filtered by audience + department + active-only */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">
              {form.target_audience === "fahrer" ? "Fahrer auswählen *" : "Spezifische Person (optional)"}
            </label>
            <EmployeePicker
              value={form.assigned_to_employee_id || ""}
              onChange={(id) => setForm({ ...form, assigned_to_employee_id: id })}
              placeholder={
                form.target_audience === "fahrer"
                  ? (form.department_id ? "— Aktive Fahrer dieser Abteilung —" : "— Aktive Fahrer —")
                  : "— leer = jeder im Office kann übernehmen —"
              }
              includeInactive={false}
              filter={(e) => {
                if (form.target_audience === "fahrer") {
                  if (e.role !== "fahrer") return false;
                } else {
                  if (e.role === "fahrer") return false;
                }
                if (form.department_id && e.department_id !== form.department_id) return false;
                return true;
              }}
              testid="task-employee-picker"
            />
            {form.target_audience === "office" && !form.assigned_to_employee_id && (
              <p className="text-[10px] text-amber-300/80 mt-1">Wenn leer, kann <b>jeder Office-Kollege</b> die Aufgabe übernehmen — der erste Klick gewinnt.</p>
            )}
            {form.target_audience === "fahrer" && form.department_id && (
              <p className="text-[10px] text-zinc-500 mt-1">
                Es werden nur <b>aktive Fahrer</b> aus der gewählten Abteilung gezeigt.
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Titel *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-field-title"
                   className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Beschreibung</label>
            <textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-field-desc"
                      className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          {/* Extra notes (driver-visible) */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">
              Zusätzliche Notiz {form.target_audience === "fahrer" ? "(wird dem Fahrer im Portal angezeigt)" : "(intern)"}
            </label>
            <textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="task-field-notes"
                      placeholder="z.B. Adresse, Telefonnummer, Sonderhinweis"
                      className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          {/* Priority + Date + Time */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Priorität</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-field-priority"
                      className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="niedrig">Niedrig</option><option value="normal">Normal</option><option value="hoch">Hoch</option><option value="kritisch">Kritisch</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Fälligkeit</label>
              <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-field-due"
                     className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">Uhrzeit</label>
              <input type="time" value={form.due_time || ""} onChange={(e) => setForm({ ...form, due_time: e.target.value })} data-testid="task-field-due-time"
                     className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
          <button onClick={onSave} data-testid="task-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            {editing ? "Speichern" : "Aufgabe anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AudienceToggle({ active, icon: Icon, label, sub, tone, onClick, testid }) {
  const tones = {
    blue: active ? "bg-blue-500/15 border-blue-500 text-blue-200" : "bg-zinc-950 border-zinc-800 text-zinc-300",
    rose: active ? "bg-rose-500/15 border-rose-500 text-rose-200" : "bg-zinc-950 border-zinc-800 text-zinc-300",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`p-3 rounded-md border-2 transition-colors text-left ${tones[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5"/>
        <div>
          <div className="text-sm font-bold">{label}</div>
          <div className="text-[10px] opacity-80">{sub}</div>
        </div>
      </div>
    </button>
  );
}

function DeptFilterChip({ id, onClear }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
          data-testid="aufgaben-dept-chip">
      <span className="font-semibold">Abteilung:</span>
      <DepartmentBadge id={id} />
      <button type="button" onClick={onClear} aria-label="Filter entfernen"
              data-testid="aufgaben-dept-chip-clear"
              className="text-amber-300/70 hover:text-amber-200">
        <X className="w-3 h-3"/>
      </button>
    </span>
  );
}

